import React, { ReactNode } from "react";

interface ChartSkeletonProps {
  children?: ReactNode;
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ children }) => {
  // Generate last 12 months
  const generateLast12Months = () => {
    const months: string[] = [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const today = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push(monthNames[date.getMonth()]);
    }

    return months;
  };

  const last12Months = generateLast12Months();

  // Generate random heights for skeleton bars to create a more natural appearance
  const generateSkeletonData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      stacks: Array.from({ length: 5 }, (_, j) => ({
        id: j,
        height: Math.random() * 0.8 + 0.2, // Random height between 20% and 100%
      })),
    }));
  };

  const skeletonData = generateSkeletonData();
  const chartWidth = 860;
  const chartHeight = 360;
  const barWidth = (chartWidth - 50) / 12; // 12 bars plus margins
  const margin = 40;

  return (
    <div
      style={{
        height: `${chartHeight}px`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        backgroundColor: "var(--gray-50)",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      {/* SVG Container with skeleton bars */}
      <svg
        width="100%"
        height="100%"
        style={{ display: "block" }}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        preserveAspectRatio="none"
      >
        {/* Y-axis */}
        <line x1={margin} y1="10" x2={margin} y2={chartHeight - 30} stroke="var(--gray-200)" strokeWidth="1" />

        {/* X-axis */}
        <line
          x1={margin}
          y1={chartHeight - 30}
          x2={chartWidth - 10}
          y2={chartHeight - 30}
          stroke="var(--gray-200)"
          strokeWidth="1"
        />

        {/* Y-axis tick marks and labels */}
        {Array.from({ length: 5 }, (_, i) => {
          const y = 20 + (i * (chartHeight - 50)) / 4;
          const value = (4 - i) * 100;
          return (
            <g key={`y-tick-${i}`}>
              <line x1={margin - 5} y1={y} x2={margin} y2={y} stroke="var(--gray-300)" strokeWidth="1" />
              <text
                x={margin - 10}
                y={y + 3}
                textAnchor="end"
                fontSize="12"
                fill="var(--gray-400)"
                className={`skeleton-text`}
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Skeleton bars */}
        {skeletonData.map((dateGroup, dateIndex) => {
          const xStart = margin + dateIndex * barWidth + 5;
          let yOffset = chartHeight - 30;

          return (
            <g key={`bar-group-${dateIndex}`}>
              {dateGroup.stacks.map((stack, stackIndex) => {
                const stackHeight = (stack.height * (chartHeight - 50)) / 1.5;
                const currentY = yOffset - stackHeight;

                const barElement = (
                  <rect
                    key={`bar-${dateIndex}-${stackIndex}`}
                    x={xStart}
                    y={currentY}
                    width={barWidth - 2}
                    height={stackHeight}
                    fill="var(--gray-200)"
                    opacity="0.6"
                    className="skeleton-bar"
                    style={{
                      animation: `pulse 1.5s ease-in-out infinite`,
                      animationDelay: `${(dateIndex + stackIndex) * 0.05}s`,
                    }}
                  />
                );

                yOffset = currentY;
                return barElement;
              })}

              {/* X-axis label (month) */}
              <text
                x={xStart + barWidth / 2}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="12"
                fill="var(--gray-400)"
                className="skeleton-text"
              >
                {last12Months[dateIndex]}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Skeleton Legend */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          padding: "12px 16px",
          borderTop: "1px solid var(--gray-200)",
          flexWrap: "wrap",
        }}
      >
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={`legend-${i}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                backgroundColor: "var(--gray-300)",
                opacity: "0.5",
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.08}s`,
              }}
            />
            <div
              style={{
                height: "14px",
                width: "80px",
                backgroundColor: "var(--gray-200)",
                borderRadius: "2px",
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.08}s`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.3;
          }
        }
        
        .skeleton-bar {
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.05));
        }
      `}</style>

      {/* Children overlay - positioned on top of chart */}
      {children && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
